import { useState } from 'react';
import type { FieldErrors, SubmitHandler } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { isDniFeatureEnabled } from '@/config/featureFlags';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import { SendCustomerDataEvents } from '@/modules/shared/utils/gtmEvents';
import {
  CustomerDataEventInteractiveActions,
  CustomerDataEventInteractiveCategories,
  CustomerDataEventInteractiveTags,
  CustomerDataEventInteractiveTipoLogin
} from '@/modules/shared/utils/gtmEvents/customerDataTypes';
import useCreateShippingAddressForGuest from '@/sections/customerData/hooks/mutators/useCreateShippingAddressFotGuest/useCreateShippingAddressForGuest';
import { addressGuestFormToAddress } from '@/sections/customerData/mappers/addressGuestFormToAddress';
import type { AddressGuestFormValues } from '@/sections/customerData/types/AddressGuestFormValues';
import useCommunesOptions from '@/sections/region/hooks/useCommunesOption';
import useRegionsOptions from '@/sections/region/hooks/useRegionsOptions';

interface UseContinueToShippingReturn {
  submissionHandler: () => Promise<void>;
  isSubmitting: boolean;
}

interface HandleGuestParams {
  userData: AddressGuestFormValues;
  selectedRegion: string;
  selectedCommune: string;
}

const { DNI, RUT } = IdentificationDocumentTypes;

export const useContinueToShipping = (): UseContinueToShippingReturn => {
  const cart = useAppSelector((state) => state?.cart?.data);
  const regionOptions = useRegionsOptions();
  const shippingAddress = cart?.shippingAddress;
  const communeOptions = useCommunesOptions(shippingAddress?.region ?? 0);
  const selectedRegion =
    regionOptions?.find((region) => region.value === shippingAddress?.region)
      ?.label ?? '';

  const selectedCommune =
    communeOptions?.find(
      (commune) => commune.value === shippingAddress?.commune
    )?.label ?? '';

  const customerForm = useFormContext<AddressGuestFormValues>();
  const { handleSubmit } = customerForm;
  const {
    trigger: createShippingAddressForGuest,
    isLoading: isMutatorLoading
  } = useCreateShippingAddressForGuest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDniEnabled = isDniFeatureEnabled();

  const handleGuest = ({
    userData,
    selectedRegion,
    selectedCommune
  }: HandleGuestParams) => {
    const isDNI = isDniEnabled && userData.documentType === DNI;
    const isRUT = userData.documentType === RUT;

    const rutBase64 = btoa(userData?.customerRut ?? '');
    const dniBase64 = btoa(userData?.documentID ?? '');

    SendCustomerDataEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-category':
          CustomerDataEventInteractiveCategories.UserIdentify,
        'event-interactive-accion':
          CustomerDataEventInteractiveActions.ContinueAsGuest,
        'event-interactive-tag': CustomerDataEventInteractiveTags.GuestUser,
        i1: userData?.email ?? '',
        i2: userData?.customerRut ?? '',
        'event-interactive-rut': isRUT ? rutBase64 : undefined,
        'event-interactive-dni': isDNI ? dniBase64 : undefined,
        'event-interactive-tipoLogin':
          CustomerDataEventInteractiveTipoLogin.Guest,
        'event-interactive-region': selectedRegion ?? '',
        'event-interactive-comuna': selectedCommune ?? '',
        event: 'event-interactive',
        documentType: userData.documentType ?? RUT
      }
    });
  };

  const handleSubmitForm: SubmitHandler<AddressGuestFormValues> = async (
    formData
  ) => {
    setIsSubmitting(true);
    try {
      const processedAddress = addressGuestFormToAddress(formData);
      await createShippingAddressForGuest(processedAddress);
      handleGuest({
        userData: formData,
        selectedRegion,
        selectedCommune
      });
    } catch (error) {
      console.error('Error submitting customer data form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleError = (errors: FieldErrors<AddressGuestFormValues>): void => {
    console.log('Form validation errors:', errors);
    setIsSubmitting(false);
  };

  const submissionHandler = handleSubmit(handleSubmitForm, handleError);

  return {
    submissionHandler,
    isSubmitting: isSubmitting || isMutatorLoading
  };
};

export default useContinueToShipping;
